/**
 * @file ErrorBoundary.jsx
 * @location src/components/ui/ErrorBoundary.jsx
 * @role lazy 청크 다운로드 실패 / 자식 렌더 예외를 잡아 재로드 UI 노출
 *
 * 사용 사례:
 *   - 네트워크 끊김 상태에서 lazy() 청크 다운로드 실패
 *   - 컴포넌트 내부 render-time 예외
 *
 * React 18 의 ErrorBoundary 는 여전히 class 컴포넌트로만 구현 가능.
 *
 *   <ErrorBoundary fallback="페이지 불러오기에 실패했습니다">
 *     <Suspense fallback={...}><LazyPage /></Suspense>
 *   </ErrorBoundary>
 */
import { Component } from 'react';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] caught:', error, info?.componentStack ?? '');
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // 사용자 정의 fallback — function 이면 호출, 아니면 기본 UI
    if (typeof this.props.fallback === 'function') {
      return this.props.fallback(error, this.reset);
    }

    // 청크 다운로드 실패는 브라우저 module map 이 실패한 import URL 을 캐시하므로,
    // in-place reset(같은 lazy 재렌더)으로는 복구되지 않는다 — 풀 리로드만이 module map 을 비운다.
    // 이 경우 「재시도」 은 같은 에러로 되돌아가 사용자를 오도하므로 「재불러오기」 단일 버튼만 노출. (QA 이슈 #7)
    const isChunkError = /dynamically imported module|Failed to fetch|importing a module script failed|Loading (?:CSS )?chunk/i
      .test(String(error?.message ?? ''));

    return (
      <div style={{
        padding: 32, margin: 24, borderRadius: 'var(--r-md)',
        background: 'var(--bg2)', border: '1px solid var(--bd)',
        color: 'var(--t1)', textAlign: 'center',
      }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          ⚠ 불러오기에 실패했습니다
        </div>
        <div style={{ fontSize: 13, color: 'var(--t3)', marginBottom: 16 }}>
          {this.props.message ?? '네트워크 연결을 확인하고 다시 불러와 주세요'}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {isChunkError ? (
            <button className="btnp" onClick={() => window.location.reload()}>↻ 다시 불러오기</button>
          ) : (
            <>
              <button className="btns" onClick={this.reset}>↻ 재시도</button>
              <button className="btnp" onClick={() => window.location.reload()}>페이지 새로 고침</button>
            </>
          )}
        </div>
        {error?.message && (
          <details style={{ marginTop: 14, fontSize: 11, color: 'var(--t3)', textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer' }}>오류 상세</summary>
            <pre style={{ overflow: 'auto', marginTop: 6 }}>{String(error.message)}</pre>
          </details>
        )}
      </div>
    );
  }
}
